import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { config } from "../../../../../Helpers/env";


const USE_MOCK_DATA = false;
const CERVICAL_SCREENING_API_BASE = config.smsUrl + "/obgyn";



export const SCREENING_METHODS = [
    { value: "via", label: "VIA (Visual Inspection with Acetic Acid)" },
    { value: "vili", label: "VILI (Visual Inspection with Lugol's Iodine)" },
    { value: "pap_smear", label: "Pap Smear (Cervical Cytology)" },
    { value: "hpv_dna", label: "HPV DNA Test" },
];

export const SCREENING_RESULTS = [
    { value: "negative", label: "Negative", color: "#2e7d32" },
    { value: "positive", label: "Positive (Acetowhite Lesion / Abnormal)", color: "#e65100" },
    { value: "suspicious", label: "Suspicious for Cancer", color: "#c62828" },
    { value: "cancer_suspected", label: "Cancer Suspected — Refer", color: "#b71c1c" },
];

export const TREATMENT_OPTIONS = [
    { value: "none", label: "No Treatment Required" },
    { value: "cryotherapy", label: "Cryotherapy" },
    { value: "leep", label: "LEEP (Loop Electrosurgical Excision Procedure)" },
    { value: "thermocoagulation", label: "Thermocoagulation" },
    { value: "referral", label: "Referral for Further Management" },
];

export const HPV_VACCINATION_STATUSES = [
    { value: "vaccinated", label: "Vaccinated" },
    { value: "not_vaccinated", label: "Not Vaccinated" },
    { value: "unknown", label: "Unknown" },
];

export const HPV_VACCINATION_DOSES = [
    { value: "1", label: "1 Dose" },
    { value: "2", label: "2 Doses" },
    { value: "3", label: "3 Doses (Complete)" },
];

export const HIV_STATUS_OPTIONS = [
    { value: "positive", label: "HIV Positive" },
    { value: "negative", label: "HIV Negative" },
    { value: "unknown", label: "Unknown" },
];


export const calculateNextScreeningDate = function (screeningDate, result, hivStatus) {
    if (!screeningDate) return null;
    const date = new Date(screeningDate);
    if (isNaN(date.getTime())) return null;

    if (result === "cancer_suspected" || result === "suspicious") {
        return null; // requires referral, no routine rescreening
    }

    if (result === "positive") {
        // Treated positive: re-screen in 1 year
        date.setFullYear(date.getFullYear() + 1);
        return date.toISOString().split("T")[0];
    }

    if (result === "negative" && hivStatus === "positive") {
        // HIV-positive with negative result: annual screening
        date.setFullYear(date.getFullYear() + 1);
        return date.toISOString().split("T")[0];
    }

    // Default negative: 3 years
    date.setFullYear(date.getFullYear() + 3);
    return date.toISOString().split("T")[0];
};


export const isScreeningOverdue = function (nextDate) {
    if (!nextDate) return false;
    const next = new Date(nextDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return next < today;
};


export const isScreeningDueSoon = function (nextDate) {
    if (!nextDate) return false;
    const next = new Date(nextDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const thirtyDays = new Date(today);
    thirtyDays.setDate(thirtyDays.getDate() + 30);
    return next >= today && next <= thirtyDays;
};


const createMockScreenings = function (patient_number) {
    return [
        {
            id: 1,
            patient_number: patient_number,
            visit_id: "V-1001",
            screening_date: "2024-03-15",
            screening_method: "via",
            result: "negative",
            treatment_given: "none",
            treatment_date: null,
            hpv_vaccination_status: "not_vaccinated",
            hpv_vaccination_doses: null,
            hiv_status_at_screening: "negative",
            next_screening_date: "2027-03-15",
            referral_facility: null,
            notes: "Cervix appeared healthy. No acetowhite lesions observed. Patient counselled on regular screening.",
            screened_by: "Dr. Sarah Nakamya",
            created_at: "2024-03-15T10:30:00Z",
        },
        {
            id: 2,
            patient_number: patient_number,
            visit_id: "V-0892",
            screening_date: "2023-06-20",
            screening_method: "via",
            result: "positive",
            treatment_given: "cryotherapy",
            treatment_date: "2023-06-20",
            hpv_vaccination_status: "not_vaccinated",
            hpv_vaccination_doses: null,
            hiv_status_at_screening: "negative",
            next_screening_date: "2024-06-20",
            referral_facility: null,
            notes: "Small acetowhite lesion on anterior lip of cervix. Eligible for cryotherapy — performed same day. Follow up in 1 year.",
            screened_by: "Dr. James Okello",
            created_at: "2023-06-20T14:15:00Z",
        },
    ];
};



const createInitialFormState = function () {
    return {
        screening_date: new Date().toISOString().split("T")[0],
        screening_method: "",
        result: "",
        treatment_given: "none",
        treatment_date: "",
        hpv_vaccination_status: "",
        hpv_vaccination_doses: "",
        hiv_status_at_screening: "",
        next_screening_date: "",
        referral_facility: "",
        notes: "",
    };
};


export const useCervicalScreening = function (actions, patient_number, visit_id) {
   
    const loadingState = useState(true);
    const loading = loadingState[0];
    const setLoading = loadingState[1];

    const submittingState = useState(false);
    const submitting = submittingState[0];
    const setSubmitting = submittingState[1];

    const screeningsState = useState([]);
    const screenings = screeningsState[0];
    const setScreenings = screeningsState[1];

    const formState = useState(createInitialFormState());
    const form = formState[0];
    const setForm = formState[1];

    const tabState = useState("screening");
    const activeTab = tabState[0];
    const setActiveTab = tabState[1];

    const editingState = useState(null);
    const editingRecord = editingState[0];
    const setEditingRecord = editingState[1];

    const procedureAllowedState = useState(null);
    const procedureAllowed = procedureAllowedState[0];
    const setProcedureAllowed = procedureAllowedState[1];

    const procedureCheckingState = useState(true);
    const procedureChecking = procedureCheckingState[0];
    const setProcedureChecking = procedureCheckingState[1];

   
    const fetchScreenings = useCallback(function () {
        setLoading(true);
        if (USE_MOCK_DATA) {
            setTimeout(function () {
                setScreenings(createMockScreenings(patient_number));
                setLoading(false);
            }, 500);
            return;
        }

        axios.get(CERVICAL_SCREENING_API_BASE + "/get_cervical_screenings", {
            params: { patient_number: patient_number },
            headers: { "Authorization": "Bearer " + sessionStorage.getItem("token") },
        }).then(function (res) {
            const data = res.data || [];
            setScreenings(Array.isArray(data) ? data : (data.screenings || []));
            setLoading(false);
        }).catch(function (err) {
            console.error("Failed to fetch cervical screenings:", err);
            setLoading(false);
            showSnackbar("error", "Failed to load screening history");
        });
    }, [patient_number, actions]);

    const checkProcedure = useCallback(function () {
        return axios.get(CERVICAL_SCREENING_API_BASE + "/check_cervical_screening", {
            params: { visit_id: visit_id },
            headers: { "Authorization": "Bearer " + sessionStorage.getItem("token") },
        }).then(function (res) {
            setProcedureAllowed(res.data.procedure_found === true);
        }).catch(function (err) {
            console.error("Failed to check cervical screening procedure:", err);
            setProcedureAllowed(false);
        }).finally(function () {
            setProcedureChecking(false);
        });
    }, [visit_id]);

    useEffect(function () {
        if (patient_number && visit_id) {
            Promise.all([fetchScreenings(), checkProcedure()]);
        }
    }, [patient_number, visit_id, fetchScreenings, checkProcedure]);

    
    const handleChange = useCallback(function (field, value) {
        setForm(function (prev) {
            const updated = Object.assign({}, prev);
            updated[field] = value;

            // Auto-calculate next screening date when result or HIV status changes
            if (field === "result" || field === "hiv_status_at_screening") {
                const screeningDate = field === "screening_date" ? value : prev.screening_date;
                const result = field === "result" ? value : prev.result;
                const hivStatus = field === "hiv_status_at_screening" ? value : prev.hiv_status_at_screening;

                if (result) {
                    const nextDate = calculateNextScreeningDate(screeningDate, result, hivStatus);
                    updated.next_screening_date = nextDate || "";
                }
            }

            // Auto-calculate next screening date when screening date changes
            if (field === "screening_date" && prev.result) {
                const nextDate2 = calculateNextScreeningDate(value, prev.result, prev.hiv_status_at_screening);
                updated.next_screening_date = nextDate2 || "";
            }

            // Clear treatment date if no treatment
            if (field === "treatment_given" && value === "none") {
                updated.treatment_date = "";
            }

            // Auto-set treatment date to screening date when treatment is selected
            if (field === "treatment_given" && value !== "none" && !prev.treatment_date) {
                updated.treatment_date = prev.screening_date;
            }

            // Clear HPV doses if not vaccinated
            if (field === "hpv_vaccination_status" && value !== "vaccinated") {
                updated.hpv_vaccination_doses = "";
            }

            // Clear referral if not referral treatment
            if (field === "treatment_given" && value !== "referral") {
                updated.referral_facility = "";
            }

            return updated;
        });
    }, []);

   
    const validationErrors = useMemo(function () {
        const errors = {};
        if (!form.screening_date) errors.screening_date = "Screening date is required";
        if (!form.screening_method) errors.screening_method = "Screening method is required";
        if (!form.result) errors.result = "Screening result is required";
        if (form.result === "positive" && form.treatment_given === "none") {
            errors.treatment_given = "Treatment or referral required for positive result";
        }
        if (form.treatment_given !== "none" && !form.treatment_date) {
            errors.treatment_date = "Treatment date is required";
        }
        if (form.treatment_given === "referral" && !form.referral_facility) {
            errors.referral_facility = "Referral facility is required";
        }
        return errors;
    }, [form]);

    const isFormValid = useMemo(function () {
        return Object.keys(validationErrors).length === 0;
    }, [validationErrors]);

    const handleSubmit = useCallback(function () {
        if (!isFormValid) {
            if (actions && actions.snackbarActions) {
                showSnackbar("error", "Please fill in all required fields");
            }
            return;
        }

        setSubmitting(true);

        const payload = Object.assign({}, form, {
            patient_number: patient_number,
            visit_id: visit_id,
        });

        if (USE_MOCK_DATA) {
            setTimeout(function () {
                if (editingRecord) {
                    // Update existing
                    setScreenings(function (prev) {
                        return prev.map(function (s) {
                            if (s.id === editingRecord.id) {
                                return Object.assign({}, s, payload, { created_at: s.created_at });
                            }
                            return s;
                        });
                    });
                    if (actions && actions.snackbarActions) {
                        showSnackbar("success", "Screening record updated successfully");
                    }
                } else {
                    // Create new
                    const newRecord = Object.assign({}, payload, {
                        id: Date.now(),
                        screened_by: "Current User",
                        created_at: new Date().toISOString(),
                    });
                    setScreenings(function (prev) { return [newRecord].concat(prev); });
                    if (actions && actions.snackbarActions) {
                        showSnackbar("success", "Screening recorded successfully");
                    }
                }
                resetForm();
                setSubmitting(false);
                setActiveTab("history");
            }, 600);
            return;
        }

        const url = editingRecord
            ? CERVICAL_SCREENING_API_BASE + "/update_cervical_screening?screening_id=" + editingRecord.id
            : CERVICAL_SCREENING_API_BASE + "/create_cervical_screening";
        const method = editingRecord ? "put" : "post";

        axios[method](url, payload, {
            headers: { "Authorization": "Bearer " + sessionStorage.getItem("token") },
        }).then(function () {
            if (actions && actions.snackbarActions) {
                showSnackbar("success", editingRecord ? "Screening record updated" : "Screening recorded successfully");
            }
            resetForm();
            fetchScreenings();
            setSubmitting(false);
            setActiveTab("history");
        }).catch(function (err) {
            console.error("Failed to save screening:", err);
            setSubmitting(false);
            if (actions && actions.snackbarActions) {
                showSnackbar("error", "Failed to save screening record");
            }
        });
    }, [form, isFormValid, editingRecord, patient_number, visit_id, actions, fetchScreenings]);

  
    const startEdit = useCallback(function (record) {
        setEditingRecord(record);
        setForm({
            screening_date: record.screening_date || "",
            screening_method: record.screening_method || "",
            result: record.result || "",
            treatment_given: record.treatment_given || "none",
            treatment_date: record.treatment_date || "",
            hpv_vaccination_status: record.hpv_vaccination_status || "",
            hpv_vaccination_doses: record.hpv_vaccination_doses || "",
            hiv_status_at_screening: record.hiv_status_at_screening || "",
            next_screening_date: record.next_screening_date || "",
            referral_facility: record.referral_facility || "",
            notes: record.notes || "",
        });
        setActiveTab("screening");
    }, []);

    /* ─── Reset ─── */
    const resetForm = useCallback(function () {
        setForm(createInitialFormState());
        setEditingRecord(null);
    }, []);

    /* ─── Cancel Edit ─── */
    const cancelEdit = useCallback(function () {
        resetForm();
    }, [resetForm]);

    /* ─── Snackbar helper ─── */
    const showSnackbar = useCallback(function (type, message) {
        if (!actions || !actions.snackbarActions) return;
        const snack = actions.snackbarActions;
        if (typeof snack.snackbarOpen === "function") {
            return snack.snackbarOpen(type, message);
        }
        if (type === "success" && typeof snack.snackSuccess === "function") {
            return snack.snackSuccess(message);
        }
        if (type === "error" && typeof snack.snackError === "function") {
            return snack.snackError(message);
        }
        if (type === "warning" && typeof snack.snackWarning === "function") {
            return snack.snackWarning(message);
        }
        if (type === "info" && typeof snack.snackInfo === "function") {
            return snack.snackInfo(message);
        }

        // fallback
        if (typeof snack.error === "function" && type === "error") {
            return snack.error(message);
        }
        if (typeof snack.success === "function" && type === "success") {
            return snack.success(message);
        }
        if (typeof snack.snackInfo === "function") {
            return snack.snackInfo(message);
        }
    }, [actions]);

    
    const summary = useMemo(function () {
        const totalScreenings = screenings.length;

        const lastScreening = screenings.length > 0
            ? screenings.reduce(function (latest, s) {
                return new Date(s.screening_date) > new Date(latest.screening_date) ? s : latest;
            })
            : null;

        const hasCurrentVisitRecord = screenings.some(function (s) {
            return s.visit_id === visit_id;
        });

        const positiveCount = screenings.filter(function (s) {
            return s.result === "positive" || s.result === "suspicious" || s.result === "cancer_suspected";
        }).length;

        const nextScreeningDue = lastScreening ? lastScreening.next_screening_date : null;
        const overdue = isScreeningOverdue(nextScreeningDue);
        const dueSoon = isScreeningDueSoon(nextScreeningDue);

        const hivPositive = lastScreening && lastScreening.hiv_status_at_screening === "positive";

        return {
            totalScreenings: totalScreenings,
            lastScreening: lastScreening,
            hasCurrentVisitRecord: hasCurrentVisitRecord,
            positiveCount: positiveCount,
            nextScreeningDue: nextScreeningDue,
            overdue: overdue,
            dueSoon: dueSoon,
            hivPositive: hivPositive,
        };
    }, [screenings, visit_id]);

 
    const alerts = useMemo(function () {
        const result = [];

        if (summary.overdue) {
            result.push({
                type: "critical",
                title: "Screening Overdue",
                detail: "This patient's cervical screening was due on " + summary.nextScreeningDue + ". Please schedule a screening.",
            });
        }

        if (summary.dueSoon && !summary.overdue) {
            result.push({
                type: "warning",
                title: "Screening Due Soon",
                detail: "Next cervical screening due on " + summary.nextScreeningDue + ".",
            });
        }

        if (summary.hivPositive) {
            result.push({
                type: "warning",
                title: "HIV-Positive — Annual Screening Required",
                detail: "Per Uganda guidelines, HIV-positive women require annual cervical cancer screening.",
            });
        }

        if (summary.lastScreening && (summary.lastScreening.result === "suspicious" || summary.lastScreening.result === "cancer_suspected")) {
            result.push({
                type: "critical",
                title: "Suspicious/Cancer Finding — Follow-Up Required",
                detail: "Last screening on " + summary.lastScreening.screening_date + " showed a suspicious result. Ensure referral and follow-up.",
            });
        }

        if (form.result === "positive") {
            result.push({
                type: "warning",
                title: "Positive Result — Treatment Required",
                detail: "VIA/VILI positive. If the lesion is eligible, treat with cryotherapy or thermocoagulation. Otherwise, refer for LEEP.",
            });
        }

        if (form.result === "cancer_suspected") {
            result.push({
                type: "critical",
                title: "Cancer Suspected — Immediate Referral",
                detail: "Refer to a facility with capacity for biopsy and staging. Document referral facility and ensure follow-up.",
            });
        }

        return result;
    }, [summary, form.result]);

    return {
        loading: loading,
        submitting: submitting,
        procedureAllowed: procedureAllowed,
        procedureChecking: procedureChecking,
        screenings: screenings,
        form: form,
        activeTab: activeTab,
        setActiveTab: setActiveTab,
        editingRecord: editingRecord,
        handleChange: handleChange,
        handleSubmit: handleSubmit,
        startEdit: startEdit,
        cancelEdit: cancelEdit,
        resetForm: resetForm,
        validationErrors: validationErrors,
        isFormValid: isFormValid,
        summary: summary,
        alerts: alerts,
    };
};
